/**
 * Utility helpers.
 */

import { getDataAttr } from 'scripts/helpers/_attributes';
import { addEventListeners } from 'scripts/helpers/_events';
import { readCookie, createCookieIfMissing } from 'scripts/modules/_cookies';
import { datadogRum } from '@datadog/browser-rum';
import { getUserCountryCode } from 'scripts/modules/_i18n';
import defaultUe from '../../../site-options/03-languages.json';

/**
 * Check if user is logged in as existing NFB client.
 *
 * @return {string} smux_login cookie value
 */
export const isNfbCustomer = () => {
	return readCookie( 'smux_login' );
};

/**
 * Adds a polyfill if needed for scroll-behaviour: smooth and anchor scrolling.
 *
 * @param {number} additionalOffset Additional offset.
 */
export const initSmoothScroll = ( additionalOffset = 0 ) => {
	const scrollAnchors = document.querySelectorAll(
		'a[href^="#"]:not( [href="#"] ):not( [data-toggle] ),[data-scroll-target]'
	);

	if ( scrollAnchors ) {
		scrollAnchors.forEach( ( anchor ) => {
			anchor.addEventListener(
				'click',
				( e ) => {
					const selector = getDataAttr( anchor, 'scroll-target' ) || anchor.hash;
					if ( isValidSelector( selector ) ) {
						const target = document.querySelector( selector );
						const header = document.querySelector( 'header' );
						if ( target?.getBoundingClientRect ) {
							e.preventDefault();
							let offset = 0;
							if ( header?.getBoundingClientRect ) {
								offset = parseInt( header.getBoundingClientRect().height );
							}
							window.scrollTo( {
								top:
									parseInt( target.getBoundingClientRect().top ) +
									parseInt( window.scrollY ) -
									offset -
									additionalOffset,
								behavior: 'smooth',
							} );
						}
					}
				},
				false
			);
		} );
	}
};

/**
 * Get domain portion from FQDN.
 *
 * @param {string} fqdn
 */
export const getDomainFromFQDN = ( fqdn ) => {
	if ( ! fqdn ) {
		return '';
	}

	const domainParts = fqdn.split( '.' );
	return domainParts.slice( domainParts.length - 2 ).join( '.' );
};

/**
 * Check if a provided url is for the same parent domain (e.g., my.freshbooks.com and www.freshbooks.com).
 *
 * @param {string} url
 */
export const isSameDomain = ( url ) => {
	if ( ! url ) {
		return false;
	}

	const { host } = parseUrl( url );

	return host && getDomainFromFQDN( host ) === getDomainFromFQDN( window.location.host );
};

/**
 * Open external links in a new tab/window and remove the nofollow tag if present.
 */
export const externalLinksPatcher = () => {
	Array.from( document.querySelectorAll( 'a' ) )
		.filter( ( a ) => {
			return (
				a.href &&
				! /^(?:tel:|mailto:|javascript:|https?:\/\/(?:[^.]*\.)?(?:freshbooks\.com|[^.]*\.freshenv\.com))/.test(
					a.href
				) &&
				a.host &&
				getDomainFromFQDN( a.host ) !== getDomainFromFQDN( window.location.host )
			);
		} )
		.forEach( ( link ) => {
			link.setAttribute( 'target', '_blank' );
			link.rel = link.rel.replace( / *nofollow */, '' );
		} );
};

/**
 * Add/remove a class (stuck) to an element with class sticky-top on event at which element sticks.
 * Refer to https://developers.google.com/web/updates/2017/09/sticky-headers.
 */
export const addStickyTopObserver = () => {
	if ( typeof window.IntersectionObserver !== 'undefined' ) {
		const stickyTopEls = document.querySelectorAll( '.sticky-top' );
		if ( stickyTopEls.length ) {
			stickyTopEls.forEach( ( element ) => {
				const computedStyles = window.getComputedStyle( element );
				const sentinel = document.createElement( 'span' );
				sentinel.classList.add( 'sentinel' );
				sentinel.style = `
				position: absolute;
				top: -${ computedStyles.top || '0' };
				left: 0;
				right: 0;
				z-index: -1;
			`;
				element.parentElement.insertBefore( sentinel, element );
				new window.IntersectionObserver( function ( entries ) {
					entries.forEach( ( entry ) => {
						if ( entry.intersectionRatio === 0 ) {
							entry.target.nextElementSibling.classList.add( 'stuck' );
						} else {
							entry.target.nextElementSibling.classList.remove( 'stuck' );
						}
					} );
				} ).observe( sentinel );
			} );
		}
	}
};

/**
 * Add/remove a class (stuck) to an element with class sticky-bottom by tracking the position of
 * sentinel inserted before sticky element.
 */
export const addStickyBottomObserver = () => {
	const stickyBottomEls = document.querySelectorAll( '.sticky-bottom' );
	if ( stickyBottomEls ) {
		stickyBottomEls.forEach( ( element ) => {
			// Sentinel.
			const sentinel = document.createElement( 'span' );
			sentinel.classList.add( 'sentinel' );
			element.parentElement.insertBefore( sentinel, element );

			const addRemoveStuckClass = () => {
				if ( ! element.classList.contains( 'sticky-bottom' ) ) {
					return false;
				}
				const isBefore =
					window.scrollY + window.innerHeight < sentinel.offsetTop + element.clientHeight;
				if ( ! isBefore && element.classList.contains( 'stuck' ) ) {
					element.classList.remove( 'stuck' );
				} else if ( isBefore && ! element.classList.contains( 'stuck' ) ) {
					element.classList.add( 'stuck' );
				}
			};
			// Scrolling past the sentinel position plus height of the sticky element.
			addEventListeners( window, 'load scroll', addRemoveStuckClass );
		} );
	}
};

/**
 * Test a CSS selector to check it won't throw client-side exceptions.
 *
 * @param {string} selector CSS selector to test for validity.
 */
export const isValidSelector = ( selector ) => {
	try {
		document.createDocumentFragment().querySelector( selector );
	} catch {
		return false;
	}
	return true;
};

/**
 * Find all links that have regionalised version and replace their urls based on user's location.
 */
export const replaceRegionalisedUrls = () => {
	const targetRegion = getUserTargetRegion();
	// eslint-disable-next-line no-undef
	const { i18n } = fbVars;

	if ( targetRegion ) {
		if (
			typeof targetRegion.sitePaths === 'object' &&
			Object.keys( targetRegion.sitePaths ).length > 0
		) {
			const linkNodes = Array.from(
				document.querySelectorAll(
					'a[href]:not(.region):not(.ab-item):not([href^="#"]):not([href*="wp-admin/"])'
				)
			);
			linkNodes.forEach( ( linkNode ) => {
				if ( linkNode.origin === i18n.domain ) {
					const formattedPathname = linkNode.pathname
						.replace( /^\//, '' )
						.replace( new RegExp( `${ i18n.siteLangCode }-${ i18n.siteCountryCode }/`, 'g' ), '' );
					if ( typeof targetRegion.sitePaths[ formattedPathname ] === 'string' ) {
						linkNode.pathname = targetRegion.sitePaths[ formattedPathname ];
					}
				}
			} );
		}
	}
};

/**
 * Parse a URL into parts.
 *
 * @param {string} input URL to parse
 * @return {Object} Parsed URL parts
 */
export const parseUrl = ( input = '' ) => {
	const output = {
		url: '',
		query: '',
		hash: '',
		origin: '',
		protocol: '',
		host: '',
		pathname: '',
	};

	if ( typeof input === 'string' && input ) {
		[ output.url, output.hash ] = input.split( '#', 2 );
		[ output.url, output.query ] = output.url.split( '?', 2 );

		const urlParts = /^(https?:)?(\/\/[^/]+)?(\/.*)?$/.exec( output.url );
		if ( urlParts && urlParts.length === 4 ) {
			[ , output.protocol, output.host, output.pathname ] = urlParts;

			if ( output.host ) {
				output.host = output.host.replace( '//', '' );

				if ( ! output.protocol ) {
					output.protocol = window.location.protocol;
					output.url = `${ output.protocol }${ output.url }`;
				}

				output.origin = `${ output.protocol }//${ output.host }`;
			}
		}

		Object.keys( output ).forEach( ( key ) => {
			if ( typeof output[ key ] === 'undefined' ) {
				output[ key ] = '';
			}
		} );
	}

	return output;
};

/**
 * Parse a query string into parameters.
 *
 * @param {string}  input         Query string or URL
 * @param {boolean} [decode=true] Should the parameter values be URI decoded?
 * @return {Object} Parsed query string parameters
 */
export const parseQueryString = ( input, decode = true ) => {
	const output = {};

	if ( typeof input === 'string' && input ) {
		const params = parseUrl( input ).query.split( '&' );

		if ( params && params.length ) {
			params.forEach( ( param ) => {
				const parts = param
					.split( '=', 2 )
					.map( ( p ) => ( decode ? decodeURIComponent( p ) : p ) );

				if ( parts.length === 2 ) {
					output[ parts[ 0 ] ] = parts[ 1 ];
				} else {
					output[ parts[ 0 ] ] = '';
				}
			} );
		}
	}

	return output;
};

/**
 * Set Trial Length cookie.
 */
export const setTrialLengthCookie = () => {
	const trialLength = document.body.dataset.tl;

	if ( trialLength ) {
		createCookieIfMissing( 'fb_web_promo', `lp${ trialLength }` );
	}
};

/**
 * Init links that open modals.
 */
export const initOpenModalLinks = () => {
	const buttonsWithModals = document.querySelectorAll( 'a[target="modal"]' );

	if ( buttonsWithModals.length > 0 ) {
		buttonsWithModals.forEach( ( buttonWithModal ) => {
			buttonWithModal.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				// eslint-disable-next-line no-undef
				$( buttonWithModal.getAttribute( 'href' ) ).modal( 'show' );
			} );
		} );
	}
};

/**
 * Set top css attribute for given element, so it will always be below the header.
 *
 * @param {HTMLElement} stickyEl Element to stick.
 * @param {number}      offset   Offset from the header.
 */
export const setStickyTop = ( stickyEl, offset = 0 ) => {
	const header = document.querySelector( 'header' );

	if ( typeof window.ResizeObserver !== 'undefined' ) {
		const resizeObserver = new window.ResizeObserver( () => {
			stickyEl.style.setProperty(
				'top',
				`${ parseInt( header.getBoundingClientRect().height ) - 1 + offset }px`,
				'important'
			);
		} );
		resizeObserver.observe( header );
	} else {
		window.addEventListener( 'resize', () => {
			stickyEl.style.setProperty(
				'top',
				`${ parseInt( header.getBoundingClientRect().height ) - 1 + offset }px`,
				'important'
			);
		} );
		window.addEventListener( 'scroll', () => {
			stickyEl.style.setProperty(
				'top',
				`${ parseInt( header.getBoundingClientRect().height ) - 1 + offset }px`,
				'important'
			);
		} );
	}
};

/**
 * An object is plain if it's created by either {}, new Object(), or Object.create(null).
 *
 * @see https://www.npmjs.com/package/is-plain-obj
 * @param {*} value Value to check.
 * @return {boolean} If is plain object.
 */
export const isPlainObject = ( value ) => {
	if ( typeof value !== 'object' || value === null || typeof value === 'undefined' ) {
		return false;
	}

	const prototype = Object.getPrototypeOf( value );
	return (
		( prototype === null ||
			prototype === Object.prototype ||
			Object.getPrototypeOf( prototype ) === null ) &&
		! ( Symbol.toStringTag in value ) &&
		! ( Symbol.iterator in value )
	);
};

/**
 * Handles fp-maintenance-notice hide logic.
 */
export const initMaintenanceNotice = () => {
	const notices = document.querySelectorAll( '.fp-maintenance-notice' );

	Array.from( notices ).forEach( ( notice ) => {
		if ( notice.dataset.noticeKey && readCookie( notice.dataset.noticeKey ) ) {
			notice.remove();
		} else {
			const closeButton = notice.querySelector( '.fp-maintenance-notice--close' );
			if ( closeButton ) {
				closeButton.addEventListener( 'click', () => {
					if ( notice.dataset.noticeKey ) {
						createCookieIfMissing( notice.dataset.noticeKey, 1, 0.5 );
					}
					notice.remove();
				} );
			}
		}
	} );
};

/**
 * Handles that "fb-show-loading-on-click" classes will show loading overlay on page.
 * Optional: shows label from "title" attribute.
 */
export const initShowLoadingOnClick = () => {
	Array.from( document.querySelectorAll( '.fb-show-loading-on-click' ) ).forEach( ( node ) => {
		const linkNode = node.tagName.toLowerCase() === 'a' ? node : node.querySelector( 'a' );
		if ( linkNode && linkNode.href ) {
			linkNode.dataset.loadingText =
				linkNode.title !== '' && linkNode.title !== undefined ? linkNode.title : 'Loading...';
			linkNode.title = '';

			linkNode.addEventListener( 'click', ( event ) => {
				event.preventDefault();
				event.stopPropagation();

				const overlay = document.createElement( 'DIV' );
				overlay.classList.add( 'fb-loading-overlay' );
				if ( event.target.dataset.loadingText ) {
					overlay.innerHTML = `<p class="h3 text-white">${ event.target.dataset.loadingText }</p>`;
				}
				document.body.appendChild( overlay );

				window.location.href = linkNode.href;

				return false;
			} );
		}
	} );
};

/**
 * Returns region based on user-country-code cookie.
 *
 * @return {Object|null} User target region.
 */
export const getUserTargetRegion = () => {
	const euDefaultFor = defaultUe.eu_default_for;
	const euDefaultCountryCode = 'eu';
	const countryCodeCookie = getUserCountryCode( 'lower' );

	if ( window.fbVars && window.fbVars.i18n ) {
		const { supportedRegions, siteLangCode } = window.fbVars.i18n;
		let countryCode = countryCodeCookie;

		if ( euDefaultFor.includes( `en-${ countryCodeCookie }` ) ) {
			countryCode = euDefaultCountryCode;
		}

		if (
			supportedRegions &&
			supportedRegions[ countryCode ] &&
			Object.keys( supportedRegions[ countryCode ] ).length > 0
		) {
			return supportedRegions[ countryCode ][ siteLangCode ]
				? supportedRegions[ countryCode ][ siteLangCode ]
				: supportedRegions[ countryCode ][ Object.keys( supportedRegions[ countryCode ] )[ 0 ] ];
		}
	}

	return null;
};

/**
 * Helper function to detect if page was opened via Cypress.
 *
 * @return {boolean} If page runs via Cypress.
 */
export const isCypress = () => {
	return typeof window.Cypress !== 'undefined';
};

/**
 * Debounce function that delays invoking func until after wait milliseconds have elapsed.
 *
 * @param {Function} func
 * @param {number}   wait
 */
export const debounce = ( func, wait ) => {
	let timeout;

	return function executedFunction( ...args ) {
		const later = () => {
			clearTimeout( timeout );
			func( ...args );
		};

		clearTimeout( timeout );
		timeout = setTimeout( later, wait );
	};
};

/**
 * Inits RUM logger.
 */
export const initRumLogger = () => {
	const env = window?.fbVars?.env ?? 'local';

	if ( 'production' === env ) {
		datadogRum.init( {
			env,
			applicationId: 'f8258c48-8364-4cab-abef-1ab54073aa09',
			clientToken: 'pubcb791412d0e597bf34fc130328b1822d',
			site: 'us5.datadoghq.com',
			service: 'freshbooks-website',
			sessionSampleRate: 10,
			sessionReplaySampleRate: 0,
			trackUserInteractions: true,
			trackResources: true,
			trackLongTasks: true,
			defaultPrivacyLevel: 'allow',
		} );
	}
};
